import { useCallback } from 'react';
import { apiService, type Conversation as ApiConversation } from '../../../services/api';
import type { ChatMessage } from '../../ChatPanel';
import logger from '../../../utils/simpleLogger';
import type { MentionFilters } from '../../../types/mention';

/**
 * Hook for message preparation: validation, conversation creation, optimistic messages.
 */

export interface UseMessagePreparerOptions {
  conversation: ApiConversation | null;
  selectedDocuments: string[];
  onMessagesUpdate: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  onConversationCreate?: (name: string) => Promise<ApiConversation | null>;
}

interface SendMessageState {
  inputMessage: string;
  contextFilters: MentionFilters;
}

export function useMessagePreparer(options: UseMessagePreparerOptions) {
  const { conversation, selectedDocuments, onMessagesUpdate, onConversationCreate } = options;

  /**
   * Validate and prepare message for sending
   */
  const validateAndPrepare = useCallback(
    (state: SendMessageState, messageTextOverride?: string) => {
      const textToSend = messageTextOverride || state.inputMessage.trim();
      const hasText = textToSend.length > 0;
      const hasFilters = Object.keys(state.contextFilters).length > 0;

      return {
        textToSend,
        hasText,
        hasFilters,
        isValid: hasText || hasFilters,
      };
    },
    []
  );

  /**
   * Ensure we have an active conversation (auto-create if needed)
   */
  const ensureConversation = useCallback(
    async (textToSend: string, hasTextOrFilters: boolean): Promise<ApiConversation | null> => {
      if (conversation) {
        return conversation;
      }

      if (!onConversationCreate || !hasTextOrFilters) {
        return null;
      }

      logger.info('🔄 Auto-creating conversation before sending first message...');
      // NOTE: Do NOT call startLoading() here — sendMessage() already calls it
      // after ensureConversation() returns. Calling it twice caused loadingCount
      // to never reach 0, leaving the "Procesando..." indicator stuck.

      const convName = textToSend.substring(0, 30) || 'Nueva conversación';
      const newConv = await onConversationCreate(convName);

      if (newConv) {
        logger.info('✅ Auto-created conversation', { id: newConv.id });
        return newConv;
      }

      throw new Error('Failed to create conversation');
    },
    [conversation, onConversationCreate]
  );

  /**
   * Add optimistic user message to UI
   */
  const addOptimisticMessage = useCallback(
    (messageText: string): ChatMessage => {
      const tempUserMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        text: messageText,
        isUser: true,
        timestamp: new Date(),
        markdown: messageText,
        selectedDocuments: selectedDocuments.length > 0 ? [...selectedDocuments] : undefined,
      };

      onMessagesUpdate((prev) => [...prev, tempUserMessage]);
      return tempUserMessage;
    },
    [selectedDocuments, onMessagesUpdate]
  );

  /**
   * Create streaming message placeholder
   */
  const createStreamingMessage = useCallback(
    (streamingMessageId: string, tempUserMessageId: string): void => {
      const streamingMessage: ChatMessage = {
        id: streamingMessageId,
        text: '',
        isUser: false,
        timestamp: new Date(),
        markdown: '',
        citations: {},
        isStreaming: true,
      };

      onMessagesUpdate((prev) => {
        const otherMessages = prev.filter((m) => {
          if (m.isUser && m.id === tempUserMessageId) return false;
          if (!m.isUser && (m.id === streamingMessageId || m.isStreaming)) return false;
          return true;
        });
        const userMsg = prev.find((m) => m.id === tempUserMessageId);
        return userMsg
          ? [...otherMessages, userMsg, streamingMessage]
          : [...otherMessages, streamingMessage];
      });
    },
    [onMessagesUpdate]
  );

  return {
    validateAndPrepare,
    ensureConversation,
    addOptimisticMessage,
    createStreamingMessage,
  };
}
